/**
 * SpacesHome - Integration in One Editorial Workspace
 *
 * Migrated 2026-05-16 to the Integration-in-One design system.
 * Pattern: editorial page-head + 3-column card grid with hover edit-icon +
 * Create/Edit/Join modals.
 *
 * Tokens scoped via .iio wrapper (App.jsx mounts it on the app shell).
 */
import { useState, useEffect, useCallback, useRef } from "react";
import { useTranslation, Trans } from "react-i18next";
import { api, getToken, isSuperAdmin } from "../utils/helpers";
import CustomerDialog, { customerErrorReason } from "../components/CustomerDialog";
import "./SpacesHome.customers.css";
import { useToast, ToastContainer } from "../components/ui";
import useSpacesHomeData from "../hooks/useSpacesHomeData";
import Skeleton from "../components/Skeleton";
import "./SpacesHome.loading.css";
import IconPicker from "../components/IconPicker";
import PolicyCredentialSource from "../components/PolicyCredentialSource";
import InstanceIcon from "../components/InstanceIcon";
import { HomeButton, HomeAction, HomeIconButton, HomeInput, HomeSelect, HomeCheckbox, HomeDialog } from "../components/SpacesHomeControls";
import { stageOrderOf } from "../utils/stages";
import SpaceTopicsEditor from "../components/SpaceTopicsEditor";
import RepoResetMaintenance from "../components/RepoResetMaintenance";
import { Tab, Tabs, Skeleton as MuiSkeleton } from "@mui/material";
import { measuredCount, sapCountMeasurement, sapCountDescription } from "../utils/monitorCounts";

// The API derives stage from the assigned labels. Names and message counts do not set it.
export function compareHomeInstances(a, b, locale) {
  return ((stageOrderOf(b.stage) ?? -1) - (stageOrderOf(a.stage) ?? -1))
    || String(a.name || "").localeCompare(String(b.name || ""), locale, { numeric: true, sensitivity: "base" })
    || String(a.id).localeCompare(String(b.id));
}

function messageCounts(stats) {
  const monitor = stats._errorMonitor;
  const errorsOnly = monitor?.enabled === true || monitor?.knownOnly === true
    || stats.mode === "errors-only" || stats._source === "error-database";
  const current = monitor?.status === "active" && monitor.historyComplete === true && !monitor.lastError;
  return {
    total: measuredCount(stats.total),
    failed: stats.failed == null || (errorsOnly && stats.failed === 0 && !current) ? null : stats.failed,
  };
}

// --- Inline SVG icons used in the UI ------------------------------------
const PencilIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
  </svg>
);
const CloseIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
);
const PlusIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
);

const SpacesHome = ({ spaces, loading = false, error = null, cicdStatus, onRefresh, onOpenSpace, user, onBrandingChange }) => {
  const { t, i18n } = useTranslation(["spaces", "gemeinsam", "customers"]);
  const { toasts, addToast } = useToast();

  const superadmin = !!user && isSuperAdmin(user);
  const [customers, setCustomers] = useState(null);
  const [customersLoading, setCustomersLoading] = useState(superadmin);
  const [customersError, setCustomersError] = useState(null);
  const [customerDialog, setCustomerDialog] = useState(null);
  const [refreshError, setRefreshError] = useState(null);
  const customerRequest = useRef(null);
  const customerSession = useRef({ active: false, token: superadmin ? getToken() : null });

  const loadCustomers = useCallback(async () => {
    if (!superadmin || !customerSession.current.active) return;
    customerRequest.current?.abort();
    const controller = new AbortController();
    customerRequest.current = controller;
    const current = () => !controller.signal.aborted && customerSession.current.active
      && customerSession.current.token === getToken();
    setCustomersLoading(true);
    setCustomersError(null);
    try {
      const result = await api("/api/admin/customers", { signal: controller.signal });
      if (!current()) return;
      if (!Array.isArray(result) || result.some(item => !item || typeof item.id !== "string"
        || typeof item.name !== "string" || !Array.isArray(item.spaceIds)
        || item.spaceIds.some(id => typeof id !== "string"))) {
        throw Object.assign(new Error("Invalid customers response"), { code: "INVALID_CUSTOMERS_RESPONSE" });
      }
      setCustomers(result);
    } catch (failure) {
      if (current()) setCustomersError(failure);
    } finally {
      if (current()) setCustomersLoading(false);
    }
  }, [superadmin]);

  useEffect(() => {
    const session = customerSession.current;
    session.active = true;
    if (superadmin) loadCustomers();
    return () => {
      session.active = false;
      customerRequest.current?.abort();
    };
  }, [superadmin, loadCustomers]);

  const refreshAfterCustomerSave = async () => {
    setCustomerDialog(null);
    setRefreshError(null);
    // Refresh failures are separate from saving: retry must not repeat the mutation.
    const outcomes = await Promise.allSettled([
      loadCustomers(),
      Promise.resolve().then(() => onRefresh?.()),
      Promise.resolve().then(() => onBrandingChange?.()),
    ]);
    if (!customerSession.current.active || customerSession.current.token !== getToken()) return;
    const failed = outcomes.find(result => result.status === "rejected");
    if (failed) setRefreshError(failed.reason);
  };

  const groups = [];
  const assigned = new Map();
  if (superadmin) {
    (customers || []).forEach(customer => {
      groups.push({ id: customer.id, name: customer.name, customer, spaces: [] });
      customer.spaceIds.forEach(id => assigned.set(id, customer.id));
    });
  }
  spaces.forEach(space => {
    const customerId = superadmin && !space.isShared && !space.mirrorOf
      ? assigned.get(space.id) : space.customerId;
    let group = groups.find(item => item.id === (customerId || null));
    if (!group) {
      group = { id: customerId || null, name: space.customerName, spaces: [] };
      groups.push(group);
    }
    group.spaces.push(space);
  });
  const grouped = superadmin ? (customers?.length || 0) > 0 : spaces.some(space => space.customerId);
  const visibleGroups = groups.filter(group => group.id);
  const unassigned = groups.find(group => !group.id);
  if (unassigned) visibleGroups.push(unassigned);
  else if (grouped) visibleGroups.push({ id: null, spaces: [] });
  const visualCardOrder = new Map(visibleGroups.flatMap(group => group.spaces).map((space, index) => [space.id, index]));
  const waitingForCustomers = superadmin && customers === null;

  // --- Per-space data ---------------------------------------------------
  const { spInstances, spInstStats, errors } = useSpacesHomeData(spaces, !loading);
  const initialLoading = loading && spaces.length === 0;
  const canShowNewSpace = spaces.length === 0 || spaces.some(space => Object.hasOwn(spInstances, space.id) || errors.instances[space.id]);
  const errorReason = error => ["INVALID_SPACES_RESPONSE", "INVALID_INSTANCES_RESPONSE", "INVALID_STATISTICS_RESPONSE"].includes(error?.code)
    ? t(`laden.${error.code}`) : error?.message || t("laden.unbekannt");
  // Aufgeklappte Instanz-Listen (Spaces mit >3 Instanzen)
  const [spExpanded, setSpExpanded] = useState({});
  // Zahlen folgen der gewaehlten Sprache, nicht fest dem Deutschen: "18.859"
  // heisst auf Englisch achtzehn Komma acht, nicht achtzehntausend. Ein fest
  // eingestelltes Gebietsschema verfaelscht damit die Zahl selbst.
  const gebiet = i18n.language === "en" ? "en-GB" : "de-DE";
  const fmtNum = (x) => (x ?? 0).toLocaleString(gebiet);
  const fmtCompact = (x) => x >= 1e6
    ? t("karte.millionen", { wert: (x / 1e6).toLocaleString(gebiet, { maximumFractionDigits: 1 }) })
    : fmtNum(x);

  // --- Modal state ------------------------------------------------------
  const [creating, setCreating] = useState(false);
  const [createName, setCreateName] = useState("");
  const [createDesc, setCreateDesc] = useState("");
  const [createIcon, setCreateIcon] = useState("layers");
  // Space aus Vorlage: uebernimmt die Instanzen eines bestehenden Space. Deren Verbindung
  // bleibt Eigentum der Vorlage, damit der Service Key nur an einer Stelle gepflegt wird.
  const [createVorlage, setCreateVorlage] = useState("");
  const [vorlageInstanzen, setVorlageInstanzen] = useState(null); // null = noch nicht geladen
  const [vorlageAuswahl, setVorlageAuswahl] = useState({});
  const [policyAuswahl, setPolicyAuswahl] = useState({});
  const [vorlageError, setVorlageError] = useState(null);
  const vorlageRequest = useRef(null);
  useEffect(() => () => vorlageRequest.current?.abort(), []);

  const [editing, setEditing] = useState(null);
  const [editName, setEditName] = useState("");
  const [editDesc, setEditDesc] = useState("");
  const [editIcon, setEditIcon] = useState("layers");
  const [editAutomatic, setEditAutomatic] = useState(false);
  const [editUsers, setEditUsers] = useState([]);
  const [delConfirm, setDelConfirm] = useState("");
  const [inviteLink, setInviteLink] = useState(null);
  const [inviteLoading, setInviteLoading] = useState(false);
  const [shareCopied, setShareCopied] = useState(false);
  const [editTab, setEditTab] = useState("allgemein");

  const [joining, setJoining] = useState(false);
  const [joinCode, setJoinCode] = useState("");

  // --- Handlers ---------------------------------------------------------
  const openCreate = () => {
    setCreateName(""); setCreateDesc(""); setCreateIcon("layers");
    setCreateVorlage(""); setVorlageInstanzen(null); setVorlageAuswahl({}); setVorlageError(null);
    setCreating(true);
  };
  const closeCreate = () => { vorlageRequest.current?.abort(); setCreating(false); };
  // Instanzen der gewaehlten Vorlage holen, damit der Nutzer sieht, was uebernommen wird,
  // und einzelne abwaehlen kann.
  const waehleVorlage = async (id) => {
    vorlageRequest.current?.abort();
    const controller = new AbortController();
    vorlageRequest.current = controller;
    const token = getToken();
    const current = () => !controller.signal.aborted && token === getToken();
    setCreateVorlage(id);
    setVorlageInstanzen(null); setVorlageAuswahl({}); setVorlageError(null); setPolicyAuswahl({});
    if (!id) return;
    try {
      const list = await api(`/api/spaces/${id}/instances`, { signal: controller.signal });
      if (!current()) return;
      if (!Array.isArray(list) || list.some(item => !item || typeof item.id !== "string" || typeof item.name !== "string")) {
        throw Object.assign(new Error("Invalid instances response"), { code: "INVALID_INSTANCES_RESPONSE" });
      }
      setVorlageInstanzen(list);
      setVorlageAuswahl(Object.fromEntries(list.map(i => [i.id, true])));
    } catch (failure) {
      if (current()) setVorlageError(failure);
    }
  };

  const submitCreate = async () => {
    if (!createName.trim() || (createVorlage && (vorlageError || !vorlageInstanzen?.some(i => vorlageAuswahl[i.id])))) return;
    try {
      await api("/api/spaces", {
        method: "POST",
        body: JSON.stringify({
          name: createName.trim(), description: createDesc.trim() || undefined, icon: createIcon,
          ...(createVorlage ? {
            vorlageVon: createVorlage,
            instanzen: Object.entries(vorlageAuswahl).filter(([, an]) => an).map(([id]) => id),
            policyInheritance: Object.fromEntries(Object.entries(policyAuswahl).filter(([id, binding]) => vorlageAuswahl[id] && binding).map(([id, binding]) => [id, binding.key])),
          } : {}),
        }),
      });
      {
        const anzahl = Object.values(vorlageAuswahl).filter(Boolean).length;
        addToast(createVorlage
          ? t("anlegen.erfolgVorlage", { count: anzahl })
          : t("anlegen.erfolg"), "success");
      }
      closeCreate();
      onRefresh();
    } catch (e) {
      addToast(e.message, "error");
    }
  };

  const openEdit = async (s) => {
    setEditing(s);
    setEditName(s.name || "");
    setEditDesc(s.description || "");
    setEditIcon(s.icon || "layers");
    setEditAutomatic(s.inheritance?.automatic === true);
    setDelConfirm("");
    setInviteLink(null);
    setShareCopied(false);
    setEditTab("allgemein");
    setEditUsers([]);
    if (s.isShared) return;
    try {
      const u = await api(`/api/spaces/${s.id}/users`).catch(() => []);
      setEditUsers(Array.isArray(u) ? u : []);
    } catch { setEditUsers([]); }
  };
  const closeEdit = () => { setEditing(null); setDelConfirm(""); };
  const submitEdit = async () => {
    if (!editing) return;
    const payload = {};
    if (!editing.isShared && editName.trim() && editName.trim() !== editing.name) payload.name = editName.trim();
    if (!editing.isShared && (editDesc || "") !== (editing.description || "")) payload.description = editDesc;
    if (editIcon !== editing.icon) payload.icon = editIcon;
    if (!editing.isShared && !editing.mirrorOf && editing.inheritance
      && editAutomatic !== (editing.inheritance.automatic === true)) {
      payload.vorlageVon = editing.inheritance.spaceId;
      payload.vorlageVollstaendig = editAutomatic;
    }
    if (Object.keys(payload).length === 0) { closeEdit(); return; }
    try {
      await api(`/api/spaces/${editing.id}${editing.isShared ? "/icon" : ""}`, { method: "PUT", body: JSON.stringify(payload) });
      addToast(t("bearbeiten.gespeichert"), "success");
      closeEdit();
      onRefresh();
    } catch (e) {
      addToast(e.message, "error");
    }
  };
  const submitDelete = async () => {
    if (!editing || delConfirm !== editing.name) return;
    try {
      await api(`/api/spaces/${editing.id}`, { method: "DELETE" });
      addToast(t("bearbeiten.geloescht"), "success");
      closeEdit();
      onRefresh();
    } catch (e) {
      addToast(e.message, "error");
    }
  };
  const createInvite = async () => {
    if (!editing) return;
    setInviteLoading(true); setInviteLink(null);
    try {
      const insts = spInstances[editing.id] || [];
      const r = await api(`/api/spaces/${editing.id}/invite`, {
        method: "POST",
        body: JSON.stringify({ instanceIds: insts.map(i => i.id), baseUrl: window.location.origin }),
      });
      setInviteLink(r.link);
      addToast(t("bearbeiten.einladungErstellt"), "success");
    } catch (e) {
      addToast(e.message, "error");
    }
    setInviteLoading(false);
  };
  const copyInvite = () => {
    if (!inviteLink) return;
    navigator.clipboard.writeText(inviteLink);
    setShareCopied(true);
    setTimeout(() => setShareCopied(false), 1800);
  };

  const openJoin = () => { setJoinCode(""); setJoining(true); };
  const closeJoin = () => setJoining(false);
  const submitJoin = () => {
    if (!joinCode.trim()) return;
    // Real join handled by /invite/:token route - here we redirect to it.
    const code = joinCode.trim();
    const url = code.startsWith("http") ? code : `${window.location.origin}/invite/${code}`;
    window.location.href = url;
  };

  const renderSpaceCard = (s, cardIdx) => {
          const instancesKnown = Object.hasOwn(spInstances, s.id);
          const instancesError = errors.instances[s.id];
          if (!instancesKnown && !instancesError) return <Skeleton key={s.id} variant="cards" count={1} columns={1} label={t("gemeinsam:zustand.laedt")} className="ci-space-skeleton"/>;
          const insts = spInstances[s.id] || [];
          const statsKnown = instancesKnown && insts.every(inst => Object.hasOwn(spInstStats, inst.id));
          const statsError = insts.map(inst => errors.stats[inst.id]).find(Boolean);
          const cardError = instancesError || statsError;
          const statOf = (inst) => spInstStats[inst.id] || {};
          const pendingDatabaseStats = inst => inst.monitoringDataSource === "error-database"
            && !Object.hasOwn(spInstStats, inst.id) && !errors.stats[inst.id] && !instancesError;
          const pendingLegacyStats = insts.some(inst => inst.monitoringDataSource !== "error-database"
            && !Object.hasOwn(spInstStats, inst.id) && !errors.stats[inst.id]);
          const metricSkeleton = () => <MuiSkeleton component="span" variant="text" className="ci-space-metric-skeleton"
            role="status" aria-label={t("gemeinsam:zustand.laedt")}/>;
          const rollupPending = insts.some(pendingDatabaseStats);
          const sumCount = key => insts.reduce((sum, inst) => {
            const value = messageCounts(statOf(inst))[key];
            return sum == null || value == null ? null : sum + value;
          }, 0);
          const sumTotal = sumCount("total");
          const sumFailed = sumCount("failed");
          // Production first; unassigned instances follow the named stages.
          const sorted = [...insts].sort((a, b) => compareHomeInstances(a, b, gebiet));
          const visible = sorted.slice(0, 3);
          const hidden = sorted.slice(3);
          const isOpen = !!spExpanded[s.id];
          const totalMeasurement = inst => sapCountMeasurement(statOf(inst)._counts, 24, "total");
          const countsStale = insts.some(inst => totalMeasurement(inst)?.stale);
          const ages = insts.map(i => statOf(i).ageMs);
          const ageMin = ages.length && ages.every(age => Number.isFinite(age) && age >= 0)
            ? Math.round(Math.max(...ages) / 60000) : null;
          const instRow = (inst) => {
            const st = messageCounts(statOf(inst));
            const measurement = totalMeasurement(inst);
            const countHint = sapCountDescription(measurement, t, gebiet);
            const idle = st.total === 0 && st.failed === 0 && !measurement?.stale && !instancesError && !errors.stats[inst.id];
            return (
              <HomeAction
                key={inst.id}
                type="button"
                className="iio-sp2-inst"
                title={inst.name}
                onClick={(e) => { e.stopPropagation(); onOpenSpace(s, inst); }}
              >
                <span className="iio-sp2-iname">{inst.name}</span>
                <span className="iio-sp2-num" title={countHint || undefined}><span className="v">{pendingDatabaseStats(inst) ? metricSkeleton() : st.total == null || idle ? "-" : fmtCompact(st.total)}</span><span className="l">{t("karte.verarbeitet")}{measurement?.stale ? ` · ${t("errorMonitorCosts:notice.countsStale")}` : ""}</span></span>
                <span className="iio-sp2-num"><span className="v">{pendingDatabaseStats(inst) ? metricSkeleton() : st.failed == null || idle ? "-" : fmtNum(st.failed)}</span><span className="l">{t("karte.fehler24h")}</span></span>
                <svg className="iio-sp2-go" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="m9 18 6-6-6-6"/></svg>
              </HomeAction>
            );
          };
          return (
            <article
              key={s.id}
              className="iio-space-card iio-sp2-card ci-space-arrival"
              style={{ "--ci-arrival-step": cardIdx * Math.min(1, 3 / spaces.length) }}
              tabIndex={0}
              onClick={() => onOpenSpace(s)}
              onKeyDown={(e) => { if (e.key === "Enter" && e.target === e.currentTarget) onOpenSpace(s); }}
            >
              {(
                <HomeIconButton
                  className="iio-card-edit"
                  aria-label={t("karte.bearbeitenTitel")}
                  title={t("karte.bearbeitenTitel")}
                  onClick={(e) => { e.stopPropagation(); openEdit(s); }}
                >
                  <PencilIcon/>
                </HomeIconButton>
              )}

              <div className="iio-card-head">
                <div className="iio-card-icon"><InstanceIcon name={s.icon}/></div>
                <div className="iio-card-title">
                  <h3>{s.name}</h3>
                  <div className="iio-meta">
                    {instancesKnown ? t("gemeinsam:einheit.instanz", { count: insts.length }) : t("laden.nichtVerfuegbar")}
                  </div>
                  {(() => {
                    const c = cicdStatus?.spaces?.[s.id];
                    if (!c || (!c.pipelineReady && !c.configured && !(c.geteiltMit > 0))) return null;
                    // Ecklaschen statt Pills unter dem Titel: der CI/CD-Zustand haengt wie
                    // die Stufen-Lasche an der Kartenecke. Der Status steht aussen und
                    // traegt die Eckrundung, eine Warnung rueckt links daneben.
                    return (
                      <div className="iio-card-ecklaschen">
                        {c.geteiltMit > 0 && (
                          <span className="iio-card-ecklasche iio-card-ecklasche--warn"
                            title={t("karte.repoGeteiltTitel", { count: c.geteiltMit + 1 })}>
                            {t("karte.repoGeteilt")}
                          </span>
                        )}
                        {c.pipelineReady ? (
                          <span className="iio-card-ecklasche" title={t("karte.cicdBereitTitel")}>
                            {t("karte.cicdBereit")}
                          </span>
                        ) : c.configured ? (
                          <span className="iio-card-ecklasche iio-card-ecklasche--warn" title={t("karte.cicdOhneGithubTitel")}>
                            {t("karte.cicdOhneGithub")}
                          </span>
                        ) : null}
                      </div>
                    );
                  })()}
                </div>
                {/* Kein "live"-Status mehr: er war der Normalzustand und trug keine
                    Information. Leer und Inaktiv bleiben - das sind echte Hinweise. */}
                {instancesKnown && !instancesError && insts.length === 0
                  ? <span className="iio-status iio-status--idle iio-sp2-status">{t("karte.statusLeer")}</span>
                  : statsKnown && !cardError && !countsStale && sumTotal === 0 && sumFailed === 0
                    ? <span className="iio-status iio-status--idle iio-sp2-status">{t("karte.statusInaktiv")}</span>
                    : null}
              </div>

              <div className={`iio-card-desc${!s.description ? " iio-card-desc--empty" : ""}`}>
                {s.description || t("karte.keineBeschreibung")}
              </div>

              {cardError && <div className="ci-space-data-message" role="alert">{t(instancesError ? "laden.instanzenFehler" : "laden.statistikFehler", { grund: errorReason(cardError) })}</div>}
              {pendingLegacyStats && !cardError && <div className="ci-space-data-message" role="status">{t("gemeinsam:zustand.laedt")}</div>}

              {/* First three instances in stage order; remaining instances expand below. */}
              {insts.length > 0 && (
                <div className={"iio-sp2-insts" + (isOpen ? " open" : "")}>
                  {visible.map(instRow)}
                  {hidden.length > 0 && <>
                    <div className="iio-sp2-more"><div><div className={hidden.length > 5 ? "iio-sp2-scrollwrap" : ""}>
                      {hidden.map(instRow)}
                    </div></div></div>
                    <HomeAction
                      type="button"
                      className="iio-sp2-expander"
                      onClick={(e) => { e.stopPropagation(); setSpExpanded(p => ({ ...p, [s.id]: !isOpen })); }}
                    >
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="m6 9 6 6 6-6"/></svg>
                      <span>{isOpen ? t("karte.wenigerAnzeigen") : t("karte.weitereInstanzen", { count: hidden.length })}</span>
                    </HomeAction>
                  </>}
                </div>
              )}

              <div className="iio-card-foot iio-sp2-foot">
                <div className="iio-sp2-kpi">
                  <span className="v">{instancesKnown ? insts.length : "-"}</span>
                  <span className="l">{t("karte.tenants")}</span>
                </div>
                <div className="iio-sp2-kpi">
                  <span className="v">{rollupPending ? metricSkeleton() : statsKnown && sumTotal != null ? fmtCompact(sumTotal) : "-"}</span>
                  <span className="l">{t("karte.verarbeitet24h")}</span>
                </div>
                <div className="iio-sp2-kpi">
                  <span className="v">{rollupPending ? metricSkeleton() : statsKnown && sumFailed != null ? fmtNum(sumFailed) : "-"}</span>
                  <span className="l">{t("karte.fehler24h")}</span>
                </div>
              </div>
              {(ageMin != null || countsStale) && <div className="iio-sp2-sync">{[
                countsStale ? t("errorMonitorCosts:notice.countsStale") : null,
                ageMin != null ? t("gemeinsam:zeit.stand", { zeit: ageMin <= 1 ? t("gemeinsam:zeit.geradeEben") : t("gemeinsam:zeit.vorMinuten", { count: ageMin }) }) : null,
              ].filter(Boolean).join(" · ")}</div>}
            </article>
          );
  };
  const newSpaceCard = <>
        {/* Keep the reserved slot until a real card or a failed read is visible. */}
        {canShowNewSpace ? <HomeAction
          className="iio-space-card iio-space-card--new ci-space-arrival"
          style={{ "--ci-arrival-step": Math.min(spaces.length, 3) }}
          onClick={openCreate}
        >
          <div className="iio-plus">+</div>
          <div className="iio-lbl">{t("karte.neuTitel")}</div>
          <div className="iio-sub">{t("karte.neuSub")}</div>
        </HomeAction> : <Skeleton variant="cards" count={1} columns={1} className="ci-space-skeleton"/>}
  </>;

  // --- Render -----------------------------------------------------------
  return (
    <>
      <ToastContainer toasts={toasts}/>

      {/* Page head */}
      <div className="iio-page-head">
        <div className="iio-page-head-left iio-anim-arrival">
          <div className="iio-breadcrumb">
            <span className="iio-accent">{t("kopf.brotkrume")}</span>
            <span className="iio-sep">/</span>
            <span>{t("kopf.brotkrumeAktuell")}</span>
          </div>
          <h1>{t("kopf.titel")}</h1>
          <p className="iio-lead">
            {t("kopf.einleitung")}
          </p>
        </div>
        <div className="iio-page-head-right iio-anim-arrival ci-customers-head-actions" style={{ animationDelay: "80ms" }}>
          <HomeButton className="iio-btn iio-btn--secondary" onClick={openJoin}>{t("gemeinsam:aktion.beitreten")}</HomeButton>
          {superadmin && <HomeButton type="button" className="iio-btn iio-btn--secondary"
            disabled={customers === null || customersLoading || !!customersError || loading || !!error}
            onClick={() => setCustomerDialog({ customer: null })}>{t("customers:actions.create")}</HomeButton>}
          <HomeButton className="iio-btn iio-btn--signal" onClick={openCreate}>
            <PlusIcon/>{t("kopf.neuerSpace")}
          </HomeButton>
        </div>
      </div>

      {error && (
        <div className="iio-banner iio-banner--danger ci-spaces-load-error" role="alert">
          <span>{t("laden.fehler", { grund: errorReason(error) })}</span>
          <HomeButton type="button" className="iio-btn iio-btn--secondary" onClick={onRefresh}>{t("gemeinsam:aktion.aktualisieren")}</HomeButton>
        </div>
      )}
      {superadmin && customersError && <div className="iio-banner iio-banner--danger ci-customers-load-error" role="alert">
        <span>{t("customers:errors.load", { reason: customerErrorReason(customersError, t) })}</span>
        <HomeButton type="button" className="iio-btn iio-btn--secondary" onClick={loadCustomers}>{t("customers:actions.retry")}</HomeButton>
      </div>}
      {superadmin && refreshError && <div className="iio-banner iio-banner--danger ci-customer-refresh-error" role="alert">
        <span>{t("customers:errors.refresh", { reason: customerErrorReason(refreshError, t) })}</span>
        <HomeButton type="button" className="iio-btn iio-btn--secondary" onClick={refreshAfterCustomerSave}>{t("customers:actions.retry")}</HomeButton>
      </div>}
      {(initialLoading || waitingForCustomers) && !error && !customersError && <Skeleton variant="cards" count={3} label={t("gemeinsam:zustand.laedt")} className="ci-spaces-skeleton"/>}
      {/* Each card arrives when its instance list is available, independently of statistics. */}
      {!initialLoading && !waitingForCustomers && !(error && spaces.length === 0) && (grouped
        ? <div className="ci-customer-groups" aria-busy={customersLoading}>
          {visibleGroups.map(group => <section className="ci-customer-group" key={group.id || "unassigned"}
            aria-label={group.id ? group.name : t("customers:groups.unassigned")}>
            <header className="ci-customer-group-head">
              <h2>{group.id ? group.name : t("customers:groups.unassigned")}</h2>
              <span className="ci-customer-group-line" aria-hidden="true"/>
              {superadmin && group.customer && <HomeButton type="button" className="iio-btn iio-btn--secondary"
                aria-label={t("customers:actions.editNamed", { name: group.name })}
                disabled={customersLoading || !!customersError || loading || !!error}
                onClick={() => setCustomerDialog({ customer: group.customer })}>{t("customers:actions.edit")}</HomeButton>}
            </header>
            {group.spaces.length > 0 || !group.id
              ? <div className="iio-grid">
                {group.spaces.map(space => renderSpaceCard(space, visualCardOrder.get(space.id)))}
                {!group.id && newSpaceCard}
              </div>
              : <p className="ci-customer-group-empty">{t("customers:groups.empty")}</p>}
          </section>)}
        </div>
        : <div className="iio-grid">{spaces.map(renderSpaceCard)}{newSpaceCard}</div>)}
      {superadmin && customerDialog && <CustomerDialog customer={customerDialog.customer} customers={customers}
        spaces={spaces} spaceInstances={spInstances} instanceErrors={errors.instances}
        onClose={() => setCustomerDialog(null)} onSaved={refreshAfterCustomerSave}/>}

      {/* =====================  CREATE MODAL  ===================== */}
      {creating && (
        <HomeDialog onClose={closeCreate} aria-labelledby="iio-create-title">
            <header className="iio-modal-head">
              <div className="iio-modal-head-left">
                <span className="iio-modal-eyebrow">{t("gemeinsam:aktion.anlegen")}</span>
                <h2 id="iio-create-title">{t("anlegen.titel")}</h2>
              </div>
              <HomeIconButton className="iio-modal-close" aria-label={t("gemeinsam:aktion.schliessen")} onClick={closeCreate}><CloseIcon/></HomeIconButton>
            </header>

            <div className="iio-modal-body">
              <div className="iio-field">
                <label className="iio-field-label" htmlFor="iio-create-name">{t("feld.name")}</label>
                <HomeInput id="iio-create-name" className="iio-input" autoFocus value={createName} onChange={(e) => setCreateName(e.target.value)} placeholder={t("anlegen.namePlatzhalter")}/>
                <div className="iio-field-hint">{t("anlegen.nameHinweis")}</div>
              </div>

              <div className="iio-field">
                <label className="iio-field-label" htmlFor="iio-create-desc">{t("feld.beschreibung")}</label>
                <HomeInput multiline minRows={3} id="iio-create-desc" className="iio-textarea" value={createDesc} onChange={(e) => setCreateDesc(e.target.value)} placeholder={t("feld.beschreibungPlatzhalter")}/>
                <div className="iio-field-hint">{t("anlegen.beschreibungHinweis")}</div>
              </div>

              {/* Aus Vorlage: nur anbieten, wenn es einen Space mit Instanzen gibt. Ein leeres
                  Auswahlfeld wäre ein Versprechen ohne Inhalt. */}
              {spaces.filter(sp => sp.instanceCount > 0 && !sp.isShared && !sp.mirrorOf).length > 0 && (
                <div className="iio-field">
                  <label id="iio-create-vorlage-label" className="iio-field-label" htmlFor="iio-create-vorlage">{t("anlegen.vorlage")}</label>
                  <HomeSelect aria-label={t("anlegen.vorlage")} id="iio-create-vorlage" className="iio-input" value={createVorlage} onChange={e => waehleVorlage(e.target.value)}>
                    <option value="">{t("anlegen.vorlageOhne")}</option>
                    {/* Nur eigene Spaces: das Backend weist geteilte als Vorlage mit 403 ab, ein Angebot
                        waere hier also ein Weg, der nicht funktioniert. */}
                    {spaces.filter(sp => sp.instanceCount > 0 && !sp.isShared && !sp.mirrorOf).map(sp => (
                      <option key={sp.id} value={sp.id}>{t("anlegen.vorlageOption", { name: sp.name, count: sp.instanceCount })}</option>
                    ))}
                  </HomeSelect>
                  <div className="iio-field-hint">
                    {createVorlage
                      ? t("anlegen.vorlageHinweisGewaehlt")
                      : t("anlegen.vorlageHinweisOhne")}
                  </div>
                </div>
              )}

              {/* Vorbelegt sind alle Instanzen, weil das der häufigere Fall ist. Abwählen bleibt möglich. */}
              {createVorlage && (
                <div className="iio-field" role="group" aria-labelledby="iio-vorlage-auswahl-lbl">
                  <span className="iio-field-label" id="iio-vorlage-auswahl-lbl">{t("anlegen.auswahlLabel")}</span>
                  {vorlageError ? (
                    <div role="alert" className="iio-field-hint">
                      {t("laden.instanzenFehler", { grund: errorReason(vorlageError) })}
                      <HomeButton type="button" className="iio-btn iio-btn--secondary" onClick={() => waehleVorlage(createVorlage)}>{t("anlegen.vorlageErneut")}</HomeButton>
                    </div>
                  ) : vorlageInstanzen === null ? (
                    <div className="iio-field-hint">{t("anlegen.auswahlLaedt")}</div>
                  ) : vorlageInstanzen.length === 0 ? (
                    <div className="iio-field-hint" style={{ marginTop: 4, padding: "8px 10px", borderLeft: "3px solid var(--iio-warning)", background: "color-mix(in srgb, var(--iio-warning) 6%, var(--iio-bg-1))", borderRadius: "var(--iio-r-sm)", color: "var(--iio-text-2)" }}>{t("anlegen.auswahlLeer")}</div>
                  ) : (
                    <div style={{ display: "grid", gap: 6 }}>
                      {vorlageInstanzen.map(i => (
                        <div key={i.id} style={{ minWidth: 0 }}><label style={{ display: "grid", gridTemplateColumns: "auto minmax(0,1fr)", gap: 8, alignItems: "center", fontSize: 15, cursor: "pointer" }}>
                          <HomeCheckbox checked={!!vorlageAuswahl[i.id]}
                            onChange={e => setVorlageAuswahl(p => ({ ...p, [i.id]: e.target.checked }))}/>
                          <span style={{ minWidth: 0, overflowWrap: "anywhere" }}>
                            <b style={{ color: "var(--iio-text-1)" }}>{i.name}</b>
                            {i.stage && <span style={{ color: "var(--iio-text-3)" }}> · {i.stage.toUpperCase()}</span>}
                          </span>
                        </label>
                        {vorlageAuswahl[i.id] && <PolicyCredentialSource SelectComponent={HomeSelect} ButtonComponent={HomeButton} creating value={policyAuswahl[i.id] || null}
                          source={{ instanceId: i.id, name: i.name, credentials: (i.policyCredentials?.length ? i.policyCredentials : i.policyUsername ? [{ key: "", username: i.policyUsername, hasPassword: i.hasPolicyPassword }] : []).map(c => ({ ...c, available: c.hasPassword === true && !i.policyInheritanceError })) }}
                          onChange={binding => setPolicyAuswahl(previous => ({ ...previous, [i.id]: binding }))} />}
                        </div>
                      ))}
                    </div>
                  )}
                  {!vorlageError && vorlageInstanzen?.length > 0 && <div className="iio-field-hint" role="status">
                    {t(vorlageInstanzen.every(i => vorlageAuswahl[i.id]) ? "anlegen.vorlageVollstaendig" : "anlegen.vorlageTeilweise")}
                  </div>}
                </div>
              )}

              <div className="iio-field">
                <span className="iio-field-label">{t("feld.symbol")}</span>
                <IconPicker ButtonComponent={HomeAction} value={createIcon} onChange={setCreateIcon}/>
              </div>
            </div>

            <footer className="iio-modal-foot">
              <div className="iio-modal-foot-left"/>
              <div className="iio-modal-foot-right">
                <HomeButton className="iio-btn iio-btn--secondary" onClick={closeCreate}>{t("gemeinsam:aktion.abbrechen")}</HomeButton>
                {/* Die Beschriftung nennt den Unterschied: aus einer Vorlage entstehen auch Instanzen. */}
                <HomeButton className="iio-btn iio-btn--signal" onClick={submitCreate}
                  disabled={!createName.trim() || (!!createVorlage && (!!vorlageError || !vorlageInstanzen?.some(i => vorlageAuswahl[i.id])))}>
                  {createVorlage ? t("anlegen.knopfVorlage") : t("anlegen.knopf")}
                </HomeButton>
              </div>
            </footer>
        </HomeDialog>
      )}

      {/* =====================  EDIT MODAL  ===================== */}
      {editing && (() => {
        const showMaintenance = superadmin && !!cicdStatus?.spaces?.[editing.mirrorOf || editing.id]?.pipelineReady;
        return (
        <HomeDialog onClose={closeEdit} aria-labelledby="iio-edit-title">
            <header className="iio-modal-head">
              <div className="iio-modal-head-left">
                <span className="iio-modal-eyebrow">{t("gemeinsam:aktion.bearbeiten")}</span>
                <h2 id="iio-edit-title">{t("bearbeiten.titel")}</h2>
              </div>
              <HomeIconButton className="iio-modal-close" aria-label={t("gemeinsam:aktion.schliessen")} onClick={closeEdit}><CloseIcon/></HomeIconButton>
            </header>

            {showMaintenance && (
              <Tabs value={editTab} onChange={(_, v) => setEditTab(v)}
                sx={{ px: 2, borderBottom: "1px solid var(--iio-border-subtle)", minHeight: 36,
                  "& .MuiTab-root": { minHeight: 36, textTransform: "none", fontSize: 13, fontWeight: 500 } }}>
                <Tab value="allgemein" label={t("wartung.tabAllgemein")}/>
                <Tab value="wartung" label={t("wartung.tab")}/>
              </Tabs>
            )}

            {editTab === "allgemein" && (
            <div className="iio-modal-body">
              <div className="iio-field">
                <label className="iio-field-label" htmlFor="iio-edit-name">{t("feld.name")}</label>
                <HomeInput id="iio-edit-name" className="iio-input" disabled={editing.isShared} value={editName} onChange={(e) => setEditName(e.target.value)}/>
              </div>

              <div className="iio-field">
                <label className="iio-field-label" htmlFor="iio-edit-desc">{t("feld.beschreibung")}</label>
                <HomeInput multiline minRows={3} id="iio-edit-desc" className="iio-textarea" disabled={editing.isShared} value={editDesc} onChange={(e) => setEditDesc(e.target.value)} placeholder={t("feld.beschreibungPlatzhalter")}/>
                <div className="iio-field-hint">{t("bearbeiten.beschreibungHinweis")}</div>
              </div>

              <div className="iio-field">
                <span className="iio-field-label">{t("feld.symbol")}</span>
                <IconPicker ButtonComponent={HomeAction} value={editIcon} onChange={setEditIcon}/>
                <div className="iio-field-hint">{t("bearbeiten.symbolPersoenlich")}</div>
              </div>

              {!editing.isShared && !editing.mirrorOf && editing.inheritance && (
                <div className="iio-field">
                  <label style={{ display: "flex", alignItems: "center", gap: 10, minHeight: 44, cursor: "pointer" }}>
                    <HomeCheckbox id="iio-edit-inheritance" checked={editAutomatic} onChange={e => setEditAutomatic(e.target.checked)}
                      aria-describedby="space-inheritance-hint"/>
                    <span>{t("bearbeiten.vorlageAutomatisch")}</span>
                  </label>
                  <div id="space-inheritance-hint" className="iio-field-hint" style={{ overflowWrap: "anywhere" }}>
                    {t("bearbeiten.vorlageHinweis", { name: editing.inheritance.name })}
                    {editing.inheritance.legacy && <p>{t("bearbeiten.vorlageAlt")}</p>}
                    {editing.inheritance.missingCount > 0 && <p>{t("bearbeiten.vorlageFehlend", { count: editing.inheritance.missingCount })}</p>}
                  </div>
                </div>
              )}

              <SpaceTopicsEditor key={editing.mirrorOf || editing.id} spaceId={editing.mirrorOf || editing.id}/>

              {/* Share / invite */}
              {!editing.isShared && (
                <div className="iio-field">
                  <span className="iio-field-label">{t("bearbeiten.einladung")}</span>
                  <div className="iio-join-info">
                    {t("bearbeiten.einladungInfo")}
                  </div>
                  {!inviteLink ? (
                    <HomeButton
                      className="iio-btn iio-btn--secondary"
                      style={{ marginTop: 8, alignSelf: "flex-start" }}
                      onClick={createInvite}
                      disabled={inviteLoading || (spInstances[editing.id] || []).length === 0}
                    >
                      {inviteLoading ? t("bearbeiten.einladungLaeuft") : t("bearbeiten.einladungErstellen")}
                    </HomeButton>
                  ) : (
                    <div className="iio-home-inline" style={{ marginTop: 8 }}>
                      <HomeInput aria-label={t("bearbeiten.einladung")} readOnly value={inviteLink}/>
                      <HomeButton className="iio-btn iio-btn--secondary" onClick={copyInvite}>
                        {shareCopied ? t("bearbeiten.kopiert") : t("bearbeiten.kopieren")}
                      </HomeButton>
                    </div>
                  )}
                  {(spInstances[editing.id] || []).length === 0 && (
                    <div className="iio-field-hint">{t("bearbeiten.einladungBrauchtInstanzen")}</div>
                  )}
                </div>
              )}

              {/* Members */}
              {editUsers.length > 0 && (
                <div className="iio-field">
                  <span className="iio-field-label">{t("bearbeiten.zugriff", { count: editUsers.length })}</span>
                  <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                    {editUsers.map((u, i) => (
                      <div key={i} style={{
                        display: "flex", alignItems: "center", gap: 10,
                        padding: "8px 12px",
                        border: "1px solid var(--iio-border-subtle)",
                        borderRadius: "var(--iio-r-sm)",
                        background: "var(--iio-bg-1)",
                        fontSize: 13,
                      }}>
                        <span style={{
                          width: 24, height: 24, borderRadius: "var(--iio-r-sm)",
                          background: "var(--iio-brand-ink)", color: "var(--iio-hero-text)",
                          display: "inline-flex", alignItems: "center", justifyContent: "center",
                          fontSize: 10, fontWeight: 600,
                        }}>
                          {(u.displayName || u.username || "?")[0]?.toUpperCase()}
                        </span>
                        <span style={{ flex: 1 }}>{u.displayName || u.username}</span>
                        {u.email && <span style={{ fontFamily: "var(--iio-font-mono)", fontSize: 11, color: "var(--iio-text-3)" }}>{u.email}</span>}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Delete confirmation */}
              {!editing.isShared && (
                <div className="iio-field">
                  <span className="iio-field-label" style={{ color: "var(--iio-critical)" }}>{t("loeschen.titel")}</span>
                  <div style={{
                    padding: 12,
                    border: "1px solid var(--iio-critical)",
                    borderLeft: "3px solid var(--iio-critical)",
                    borderRadius: "var(--iio-r-sm)",
                    background: "var(--iio-bg-1)",
                    display: "flex", flexDirection: "column", gap: 10,
                  }}>
                    <div style={{ fontSize: 13, color: "var(--iio-text-1)" }}>
                      <Trans t={t} i18nKey="loeschen.hinweis" values={{ name: editing.name }}/>
                    </div>
                    <div className="iio-home-inline">
                      <HomeInput aria-label={t("loeschen.titel")} value={delConfirm} onChange={(e) => setDelConfirm(e.target.value)} placeholder={editing.name}/>
                      <HomeButton className="iio-btn iio-btn--danger" disabled={delConfirm !== editing.name} onClick={submitDelete}>{t("gemeinsam:aktion.loeschen")}</HomeButton>
                    </div>
                  </div>
                </div>
              )}
            </div>
            )}

            {editTab === "wartung" && showMaintenance && (
              <div className="iio-modal-body">
                <RepoResetMaintenance spaceId={editing.mirrorOf || editing.id} spaceName={editing.name}/>
              </div>
            )}

            {editTab === "allgemein" && (
            <footer className="iio-modal-foot">
              <div className="iio-modal-foot-left"/>
              <div className="iio-modal-foot-right">
                <HomeButton className="iio-btn iio-btn--secondary" onClick={closeEdit}>{t("gemeinsam:aktion.abbrechen")}</HomeButton>
                <HomeButton className="iio-btn iio-btn--signal" onClick={submitEdit}>{t("bearbeiten.speichern")}</HomeButton>
              </div>
            </footer>
            )}
        </HomeDialog>
        );
      })()}

      {/* =====================  JOIN MODAL  ===================== */}
      {joining && (
        <HomeDialog onClose={closeJoin} aria-labelledby="iio-join-title">
            <header className="iio-modal-head">
              <div className="iio-modal-head-left">
                <span className="iio-modal-eyebrow">{t("gemeinsam:aktion.beitreten")}</span>
                <h2 id="iio-join-title">{t("beitreten.titel")}</h2>
              </div>
              <HomeIconButton className="iio-modal-close" aria-label={t("gemeinsam:aktion.schliessen")} onClick={closeJoin}><CloseIcon/></HomeIconButton>
            </header>

            <div className="iio-modal-body">
              <div className="iio-join-info">
                {t("beitreten.info")}
              </div>
              <div className="iio-field">
                <label className="iio-field-label" htmlFor="iio-join-code">{t("beitreten.codeLabel")}</label>
                <HomeInput id="iio-join-code" className="iio-input" autoFocus value={joinCode} onChange={(e) => setJoinCode(e.target.value)} placeholder={t("beitreten.codePlatzhalter")}/>
              </div>
            </div>

            <footer className="iio-modal-foot">
              <div className="iio-modal-foot-left"/>
              <div className="iio-modal-foot-right">
                <HomeButton className="iio-btn iio-btn--secondary" onClick={closeJoin}>{t("gemeinsam:aktion.abbrechen")}</HomeButton>
                <HomeButton className="iio-btn iio-btn--signal" onClick={submitJoin} disabled={!joinCode.trim()}>{t("gemeinsam:aktion.beitreten")}</HomeButton>
              </div>
            </footer>
        </HomeDialog>
      )}
    </>
  );
};

// A new account, role or token starts a fresh customer scope and aborts old requests.
export default function SpacesHomeSession(props) {
  const sessionKey = JSON.stringify([props.user?.id, props.user?.role, props.user ? getToken() : null]);
  return <SpacesHome key={sessionKey} {...props}/>;
}
